import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  Slider,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface WeightingProfile {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  [key: string]: any; // Allow for dynamic weight fields
}

interface WeightingProfileDialogProps {
  open: boolean;
  profile: WeightingProfile | null;
  onClose: (success?: boolean) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`weights-tabpanel-${index}`}
      aria-labelledby={`weights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface WeightField {
  field: string;
  label: string;
}

// Define weight fields by category
const twelveMonthWeights: WeightField[] = [
  { field: '12_mo_adr_weight', label: 'ADR' },
  { field: '12_mo_adr_chg_weight', label: 'ADR Change' },
  { field: '12_mo_average_price_per_building_weight', label: 'Avg Price Per Building' },
  { field: '12_mo_delivered_building_chg_weight', label: 'Delivered Building Change' },
  { field: '12_mo_delivered_buildings_weight', label: 'Delivered Buildings' },
  { field: '12_mo_delivered_room_chg_pct_weight', label: 'Delivered Room Change %' },
  { field: '12_mo_delivered_rooms_weight', label: 'Delivered Rooms' },
  { field: '12_mo_demand_weight', label: 'Demand' },
  { field: '12_mo_demand_chg_weight', label: 'Demand Change' },
  { field: '12_mo_inventory_growth_weight', label: 'Inventory Growth' },
  { field: '12_mo_occupancy_weight', label: 'Occupancy' },
  { field: '12_mo_occupancy_chg_weight', label: 'Occupancy Change' },
  { field: '12_mo_opened_buildings_weight', label: 'Opened Buildings' },
  { field: '12_mo_opened_rooms_weight', label: 'Opened Rooms' },
  { field: '12_mo_revenue_weight', label: 'Revenue' },
  { field: '12_mo_revenue_chg_weight', label: 'Revenue Change' },
  { field: '12_mo_revpar_weight', label: 'RevPAR' },
  { field: '12_mo_revpar_chg_weight', label: 'RevPAR Change' },
  { field: '12_mo_sales_vol_chg_weight', label: 'Sales Volume Change' },
  { field: '12_mo_sales_volume_weight', label: 'Sales Volume' },
  { field: '12_mo_supply_weight', label: 'Supply' },
  { field: '12_mo_supply_chg_weight', label: 'Supply Change' },
  { field: '12_mo_transactions_weight', label: 'Transactions' },
  { field: '12_mo_transactions_chg_weight', label: 'Transactions Change' }
];

const threeMonthWeights: WeightField[] = [
  { field: '3_mo_adr_weight', label: 'ADR' },
  { field: '3_mo_adr_chg_weight', label: 'ADR Change' },
  { field: '3_mo_demand_weight', label: 'Demand' },
  { field: '3_mo_demand_chg_weight', label: 'Demand Change' },
  { field: '3_mo_occupancy_weight', label: 'Occupancy' },
  { field: '3_mo_occupancy_chg_weight', label: 'Occupancy Change' },
  { field: '3_mo_revenue_weight', label: 'Revenue' },
  { field: '3_mo_revenue_chg_weight', label: 'Revenue Change' },
  { field: '3_mo_revpar_weight', label: 'RevPAR' },
  { field: '3_mo_revpar_chg_weight', label: 'RevPAR Change' },
  { field: '3_mo_supply_weight', label: 'Supply' },
  { field: '3_mo_supply_chg_weight', label: 'Supply Change' }
];

const currentPeriodWeights: WeightField[] = [
  { field: 'adr_weight', label: 'ADR' },
  { field: 'adr_chg_yoy_weight', label: 'ADR Change YoY' },
  { field: 'asset_value_weight', label: 'Asset Value' },
  { field: 'avg_rooms_per_building_weight', label: 'Avg Rooms Per Building' },
  { field: 'demand_weight', label: 'Demand' },
  { field: 'demand_chg_yoy_weight', label: 'Demand Change YoY' },
  { field: 'existing_buildings_weight', label: 'Existing Buildings' },
  { field: 'inventory_rooms_weight', label: 'Inventory Rooms' },
  { field: 'market_cap_rate_weight', label: 'Market Cap Rate' },
  { field: 'market_cap_rate_growth_weight', label: 'Market Cap Rate Growth' },
  { field: 'market_sale_price_per_room_weight', label: 'Market Sale Price Per Room' },
  { field: 'market_sale_price_per_room_growth_weight', label: 'Market Sale Price Per Room Growth' },
  { field: 'occupancy_weight', label: 'Occupancy' },
  { field: 'occupancy_chg_yoy_weight', label: 'Occupancy Change YoY' },
  { field: 'revenue_weight', label: 'Revenue' },
  { field: 'revenue_chg_yoy_weight', label: 'Revenue Change YoY' },
  { field: 'revpar_weight', label: 'RevPAR' },
  { field: 'revpar_chg_yoy_weight', label: 'RevPAR Change YoY' },
  { field: 'supply_weight', label: 'Supply' },
  { field: 'supply_chg_yoy_weight', label: 'Supply Change YoY' },
  { field: 'under_construction_buildings_weight', label: 'Under Construction Buildings' },
  { field: 'under_construction_rooms_weight', label: 'Under Construction Rooms' }
];

const ytdAndAdditionalWeights: WeightField[] = [
  { field: 'ytd_adr_weight', label: 'YTD ADR' },
  { field: 'ytd_adr_chg_weight', label: 'YTD ADR Change' },
  { field: 'ytd_demand_weight', label: 'YTD Demand' },
  { field: 'ytd_demand_chg_weight', label: 'YTD Demand Change' },
  { field: 'ytd_occupancy_weight', label: 'YTD Occupancy' },
  { field: 'ytd_occupancy_chg_weight', label: 'YTD Occupancy Change' },
  { field: 'ytd_revenue_weight', label: 'YTD Revenue' },
  { field: 'ytd_revenue_chg_weight', label: 'YTD Revenue Change' },
  { field: 'ytd_revpar_weight', label: 'YTD RevPAR' },
  { field: 'ytd_revpar_chg_weight', label: 'YTD RevPAR Change' },
  { field: 'ytd_supply_weight', label: 'YTD Supply' },
  { field: 'ytd_supply_chg_weight', label: 'YTD Supply Change' },
  { field: 'covid_recovery_weight', label: 'COVID Recovery' }
];

export const WeightingProfileDialog: React.FC<WeightingProfileDialogProps> = ({
  open,
  profile,
  onClose
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [weights, setWeights] = useState<{ [key: string]: number }>({});

  // Calculate total weight sum and active weight count
  const allWeightFields = useMemo(
    () => [...twelveMonthWeights, ...threeMonthWeights, ...currentPeriodWeights, ...ytdAndAdditionalWeights],
    []
  );

  const weightSummary = useMemo(() => {
    let totalWeight = 0;
    let activeWeightCount = 0;

    allWeightFields.forEach(({ field }) => {
      const value = weights[field] || 0;
      totalWeight += value;
      if (value > 0) activeWeightCount++;
    });

    return {
      totalWeight,
      activeWeightCount,
      totalFields: allWeightFields.length,
      isValid: totalWeight > 0
    };
  }, [weights, allWeightFields]);

  // Fetch full profile data when editing
  useEffect(() => {
    const loadProfile = async () => {
      if (profile?.id && open) {
        // Editing existing profile - fetch full data
        setFetchingProfile(true);
        setError(null);
        try {
          const response = await authenticatedFetch(
            `/api/marketcompass/admin/weighting-profiles/${profile.id}`
          );

          if (!response) {
            throw new Error('No response from server');
          }

          if (!response.ok) {
            throw new Error('Failed to load profile');
          }

          const result = await response.json();
          const fullProfile = result.data;

          setName(fullProfile.name);
          setDescription(fullProfile.description || '');
          setIsActive(fullProfile.is_active);

          // Initialize weights from full profile data
          const initialWeights: { [key: string]: number } = {};
          [...twelveMonthWeights, ...threeMonthWeights, ...currentPeriodWeights, ...ytdAndAdditionalWeights].forEach(
            ({ field }) => {
              initialWeights[field] = fullProfile[field] || 0;
            }
          );
          setWeights(initialWeights);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load profile');
        } finally {
          setFetchingProfile(false);
        }
      } else if (profile && !profile.id && open) {
        // Cloning profile - data already provided, just populate form
        setName(profile.name);
        setDescription(profile.description || '');
        setIsActive(profile.is_active);

        // Initialize weights from provided profile data
        const initialWeights: { [key: string]: number } = {};
        [...twelveMonthWeights, ...threeMonthWeights, ...currentPeriodWeights, ...ytdAndAdditionalWeights].forEach(
          ({ field }) => {
            initialWeights[field] = profile[field] || 0;
          }
        );
        setWeights(initialWeights);
      } else if (!profile && open) {
        // New profile - reset form
        setName('');
        setDescription('');
        setIsActive(true);
        const initialWeights: { [key: string]: number } = {};
        [...twelveMonthWeights, ...threeMonthWeights, ...currentPeriodWeights, ...ytdAndAdditionalWeights].forEach(
          ({ field }) => {
            initialWeights[field] = 0;
          }
        );
        setWeights(initialWeights);
      }
      setTabValue(0);
    };

    loadProfile();
  }, [profile?.id, open, authenticatedFetch]);

  const handleWeightChange = (field: string, value: number) => {
    setWeights((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!weightSummary.isValid) {
      setError('At least one weight must be greater than zero');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        is_active: isActive,
        ...weights
      };

      const url = profile?.id
        ? `/api/marketcompass/admin/weighting-profiles/${profile.id}/update`
        : '/api/marketcompass/admin/weighting-profiles/create';

      const method = profile?.id ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save profile');
      }

      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderWeightSliders = (weightFields: WeightField[]) => {
    return (
      <Grid container spacing={3}>
        {weightFields.map(({ field, label }) => (
          <Grid item xs={12} key={field}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {weights[field] || 0}
                </Typography>
              </Box>
              <Slider
                value={weights[field] || 0}
                onChange={(_, value) => handleWeightChange(field, value as number)}
                min={0}
                max={50}
                marks={[
                  { value: 0, label: '0' },
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 30, label: '30' },
                  { value: 40, label: '40' },
                  { value: 50, label: '50' }
                ]}
                valueLabelDisplay="auto"
                sx={{ width: '100%' }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && !fetchingProfile && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        {profile ? 'Edit Weighting Profile' : 'Create Weighting Profile'}
      </DialogTitle>
      <DialogContent>
        {fetchingProfile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !fetchingProfile && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!fetchingProfile && (
          <>
            {/* Weight Summary Preview */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: weightSummary.isValid ? 'success.light' : 'error.light',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="medium">
                  Total Weight Sum: <strong>{weightSummary.totalWeight}</strong>
                </Typography>
                <Typography variant="body2">
                  Active Weights: <strong>{weightSummary.activeWeightCount}</strong> / {weightSummary.totalFields}
                </Typography>
              </Box>
              {!weightSummary.isValid && (
                <Typography variant="body2" color="error.dark" fontWeight="medium">
                  At least one weight must be greater than zero
                </Typography>
              )}
            </Box>

            {/* Basic Info */}
            <Box sx={{ mb: 3 }}>
              <TextField
                autoFocus
                label="Profile Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            </Box>

            {/* Weights Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label="12-Month Metrics" />
                <Tab label="3-Month Metrics" />
                <Tab label="Current Period" />
                <Tab label="YTD & Additional" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderWeightSliders(twelveMonthWeights)}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {renderWeightSliders(threeMonthWeights)}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {renderWeightSliders(currentPeriodWeights)}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {renderWeightSliders(ytdAndAdditionalWeights)}
            </TabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading || fetchingProfile}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || fetchingProfile || !name.trim() || !weightSummary.isValid}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
